import React from "react";
import { ArrowSquareOut, Sparkle } from "@phosphor-icons/react";

const CARDS = {
  dashboard: [
    {
      title: "Case prep workbook",
      body: "A printable checklist used by pro-se litigants to track filing deadlines.",
      cta: "Browse templates",
      href: "https://www.uscourts.gov/forms-rules/forms",
    },
    {
      title: "Document scanning app",
      body: "Turn paper contracts and receipts into searchable PDFs for your evidence binder.",
      cta: "Try scanner",
      href: "https://en.wikipedia.org/wiki/Document_scanner",
    },
    {
      title: "Court filing service",
      body: "File your completed complaint electronically through approved civil e-filing vendors.",
      cta: "See vendors",
      href: "https://www.uscourts.gov/court-records/electronic-filing-cmecf",
    },
  ],
  evidence: [
    {
      title: "Secure cloud storage",
      body: "Back up exhibits off-device. Keep evidence safe from drive failures.",
      cta: "Explore storage",
      href: "https://en.wikipedia.org/wiki/Cloud_storage",
    },
    {
      title: "OCR for scanned docs",
      body: "Make photographed contracts and letters fully searchable.",
      cta: "Learn OCR",
      href: "https://en.wikipedia.org/wiki/Optical_character_recognition",
    },
  ],
  "post-draft": [
    {
      title: "Notary services",
      body: "Online notaries can sign affidavits and declarations before filing.",
      cta: "Find a notary",
      href: "https://en.wikipedia.org/wiki/Online_notarization",
    },
    {
      title: "Court filing service",
      body: "E-file your final complaint through approved civil filing vendors.",
      cta: "See vendors",
      href: "https://www.uscourts.gov/court-records/electronic-filing-cmecf",
    },
    {
      title: "Legal defense funds",
      body: "Non-profits that help offset filing and service fees for qualifying users.",
      cta: "Learn more",
      href: "https://en.wikipedia.org/wiki/Legal_aid",
    },
  ],
};

const HEADERS = {
  dashboard: { title: "Recommended tools", body: "Explore services that may help strengthen your case preparation and financial position." },
  evidence: { title: "Stay organized", body: "Use tools to securely manage your documents." },
  "post-draft": { title: "Next steps", body: "Review your draft and consider tools that support preparation." },
};

export default function AdsCards({ placement = "dashboard" }) {
  const cards = CARDS[placement] || [];
  const hdr = HEADERS[placement] || HEADERS.dashboard;
  if (cards.length === 0) return null;
  return (
    <section className="mt-12 border-t border-slate-200 pt-10" data-testid={`ads-cards-${placement}`}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkle weight="duotone" className="w-5 h-5 text-slate-700" />
        <h3 className="font-serif-display text-2xl text-slate-900">{hdr.title}</h3>
      </div>
      <p className="text-sm text-slate-600 max-w-2xl mb-5">{hdr.body}</p>
      <div className={`grid grid-cols-1 md:grid-cols-${Math.min(cards.length, 3)} gap-0`}>
        {cards.map((c, i) => (
          <a
            key={c.title}
            href={c.href}
            target="_blank"
            rel="noreferrer sponsored"
            className={`group border border-slate-200 ${i > 0 ? "md:border-l-0" : ""} -mt-px md:mt-0 p-5 hover:bg-slate-50 transition-colors`}
            data-testid={`ad-card-${placement}-${i}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="font-mono-tech text-[10px] text-slate-500 uppercase tracking-wider">Sponsored · Affiliate</div>
              <ArrowSquareOut className="w-4 h-4 text-slate-400 group-hover:text-slate-900" />
            </div>
            <div className="font-serif-display text-xl leading-snug text-slate-900 group-hover:underline">{c.title}</div>
            <p className="text-sm text-slate-600 mt-2">{c.body}</p>
            <div className="mt-3 text-xs font-medium text-slate-900">{c.cta} →</div>
          </a>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-4 italic" data-testid={`ads-disclaimer-${placement}`}>
        Some links may be affiliate partnerships. This does not affect your document output.
      </p>
    </section>
  );
}
